import React, { useEffect, useState, Dispatch, SetStateAction } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { createLeaveRoster, updateLeaveRoster, getLeaveRosterDetail } from '../../services/leaveRoasterService';

const months = [
  { id: 1, name: 'January' },
  { id: 2, name: 'February' },
  { id: 3, name: 'March' },
  { id: 4, name: 'April' },
  { id: 5, name: 'May' },
  { id: 6, name: 'June' },
  { id: 7, name: 'July' },
  { id: 8, name: 'August' },
  { id: 9, name: 'September' },
  { id: 10, name: 'October' },
  { id: 11, name: 'November' },
  { id: 12, name: 'December' },
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 10 }, (_, index) => currentYear + index);

interface LeaveRosterModalProps {
  isOpen: boolean;
  editId: string | number | null;
  onClose: () => void;
  onSuccess: () => void;
  setMessage: Dispatch<SetStateAction<string>>;
  setMessageType: Dispatch<SetStateAction<'' | 'success' | 'error'>>;
}

const LeaveRosterModal: React.FC<LeaveRosterModalProps> = ({ isOpen, editId, onClose, onSuccess, setMessage, setMessageType }) => {
  const [year, setYear] = useState('');
  const [startMonth, setStartMonth] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  /*
   * Reset form when opening Add mode
   */
  useEffect(() => {
    if (isOpen && !editId) {
      setYear('');
      setStartMonth('');
      setEndMonth('');
    }
  }, [isOpen, editId]);

  /*
   * Fetch and prefill form when opening Edit mode
   */
  useEffect(() => {
    if (!isOpen || !editId) {
      return;
    }

    const fetchDetail = async () => {
      try {
        setIsLoadingDetail(true);

        const response = await getLeaveRosterDetail(editId);
        const data = response?.data?.data ?? response?.data ?? response;

        setYear(data?.year ? String(data.year) : '');
        setStartMonth(data?.start_month ? String(data.start_month) : '');
        setEndMonth(data?.end_month ? String(data.end_month) : '');
      } catch (error: any) {
        console.error('Failed to load leave roster details:', error);

        const message = error?.response?.data?.message || error?.response?.data?.error || 'Failed to load leave roster details';

        setMessage(message);
        setMessageType('error');
      } finally {
        setIsLoadingDetail(false);
      }
    };

    fetchDetail();
  }, [isOpen, editId]);

  /*
   * Reset End Month if it becomes invalid
   * (i.e. earlier than the newly selected Start Month)
   */
  useEffect(() => {
    if (startMonth && endMonth && Number(endMonth) < Number(startMonth)) {
      setEndMonth('');
    }
  }, [startMonth]);

  if (!isOpen) {
    return null;
  }

  /*
   * Submit
   */
  const handleSubmit = async () => {
    // -----------------------------
    // Validation
    // -----------------------------

    if (!year.trim()) {
      setMessage('Please select a year');
      setMessageType('error');
      return;
    }

    if (!startMonth) {
      setMessage('Please select start month');
      setMessageType('error');
      return;
    }

    if (!endMonth) {
      setMessage('Please select end month');
      setMessageType('error');
      return;
    }

    const yearNumber = Number(year);
    const startMonthNumber = Number(startMonth);
    const endMonthNumber = Number(endMonth);

    if (!Number.isInteger(yearNumber)) {
      setMessage('Please enter a valid year');
      setMessageType('error');
      return;
    }

    if (yearNumber < 2000 || yearNumber > 2100) {
      setMessage('Please enter a valid year');
      setMessageType('error');
      return;
    }

    if (startMonthNumber > endMonthNumber) {
      setMessage('End month must be after start month');
      setMessageType('error');
      return;
    }

    // -----------------------------
    // API Payload
    // -----------------------------

    const payload = {
      year: yearNumber.toString(),
      start_month: startMonthNumber,
      end_month: endMonthNumber,
    };

    console.log('Create Leave Roster Payload:', payload);

    try {
      setIsSubmitting(true);

      /*
       * CREATE
       */
      if (!editId) {
        await createLeaveRoster(payload);

        setMessage('Leave roster added successfully');
        setMessageType('success');

        onSuccess();
        onClose();

        setYear('');
        setStartMonth('');
        setEndMonth('');

        return;
      }

      /*
       * UPDATE
       */
      await updateLeaveRoster(editId, payload);

      setMessage('Leave roster updated successfully');
      setMessageType('success');

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Leave roster save error:', error);

      const message = error?.response?.data?.message || error?.response?.data?.error || 'Failed to save leave roster';

      setMessage(message);
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl rounded-xl bg-white shadow-xl">
        {/* ================= HEADER ================= */}

        <div className="flex items-center justify-between px-6 py-5">
          <h2 className="text-lg font-semibold text-slate-800">{editId ? 'Edit Leave Roster' : 'Add Leave Roster'}</h2>

          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* ================= FORM ================= */}

        <div className="grid grid-cols-3 gap-4 px-6 pb-8">
          {/* ================= YEAR ================= */}

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">Select Year</label>

            <div className="relative">
              <select
                value={year}
                onChange={(e) => setYear(e.target.value)}
                disabled={isSubmitting || isLoadingDetail}
                className="w-full appearance-none rounded-md border border-gray-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-blue-400 disabled:opacity-50"
              >
                <option value="" disabled>
                  Select Year
                </option>

                {years.map((yearOption) => (
                  <option key={yearOption} value={yearOption}>
                    {yearOption}
                  </option>
                ))}
              </select>

              <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
          </div>

          {/* ================= START MONTH ================= */}

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">Start Month</label>

            <div className="relative">
              <select
                value={startMonth}
                onChange={(e) => setStartMonth(e.target.value)}
                disabled={isSubmitting || isLoadingDetail}
                className="w-full appearance-none rounded-md border border-gray-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-blue-400 disabled:opacity-50"
              >
                <option value="" disabled>
                  Select Month
                </option>

                {months.map((month) => (
                  <option key={month.id} value={month.id}>
                    {month.name}
                  </option>
                ))}
              </select>

              <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
          </div>

          {/* ================= END MONTH ================= */}

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">End Month</label>

            <div className="relative">
              <select
                value={endMonth}
                onChange={(e) => setEndMonth(e.target.value)}
                disabled={isSubmitting || isLoadingDetail || !startMonth}
                className="w-full appearance-none rounded-md border border-gray-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-blue-400 disabled:opacity-50"
              >
                <option value="" disabled>
                  Select Month
                </option>

                {months
                  .filter((month) => !startMonth || month.id >= Number(startMonth))
                  .map((month) => (
                    <option key={month.id} value={month.id}>
                      {month.name}
                    </option>
                  ))}
              </select>

              <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
          </div>
        </div>

        {/* ================= FOOTER ================= */}

        <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-md border border-gray-200 px-5 py-2 text-sm font-medium text-slate-600 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || isLoadingDetail}
            className="rounded-md bg-red-500 px-6 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : isLoadingDetail ? 'Loading...' : editId ? 'Update' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeaveRosterModal;
